import { useFieldFocus } from 'context/FieldFocusContext'
import PropTypes from 'prop-types'
import { InputAdornment, TextField } from '@mui/material'
import { useFieldState } from 'hooks/useFieldState'
import { NumericFormat } from 'react-number-format'
import { Check, ErrorOutline } from '@mui/icons-material'
import { useRef, useEffect, useState } from 'react'

const MuiPercentageField = ({
  value,
  id,
  name,
  label,
  showAdornment = false,
  required,
  placeholder,
  onChange,
  onFocus,
  shrink,
  enableHelpText,
  helpText,
  onBlur = (e) => console.log(e),
  ...rest
}) => {
  const { handleFocus, handleBlur, registerHelpField } = useFieldFocus()
  const inputRef = useRef(null)
  const fieldId = id || name

  useEffect(() => {
    if (enableHelpText && fieldId && helpText) {
      registerHelpField(fieldId, helpText)
    }
  }, [enableHelpText, fieldId, helpText])

  const [rawValue, setRawValue] = useState(value)
  const validateFloat = (value) => {
    return `${value}`.match(/^\d+(\.\d{1,2})?%$/)
  }

  const { isError, isSuccess, handleOnblur } = useFieldState({
    required,
    validatorFunction: validateFloat,
  })

  const handleOnblurWithValidation = (e) => {
    const inputVal = e.target.value
    const isValid = validateFloat(inputVal)
    handleOnblur?.(inputVal)
    if (isValid) {
      onBlur?.(rawValue)
    }
    handleBlur()
  }

  const styles = {
    '& .MuiInputLabel-asterisk': { color: '#ea0606' },
    '& input:-webkit-autofill': {
      boxShadow: '0 0 0px 1000px white inset !important',
    },
    '&>label': {
      fontSize: '14px',
      color: '#75787B',
      top: '-2px',
    },
    '&>label.Mui-focused': {
      fontSize: '1rem',
      color: '#2370ED',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.Mui-focused.Mui-error': { color: '#ea0606' },
    '&>label.MuiFormLabel-filled, &>label.MuiInputLabel-shrink': {
      fontSize: '1rem',
      background: '#fff',
      padding: '0 5px',
      marginLeft: '-5px',
      top: 0,
    },
    '&>label.MuiFormLabel-filled.Mui-disabled': {
      top: 0,
      background:
        'linear-gradient(to bottom, #fff 0%, #fff 50%, #e9ecef 50%, #e9ecef 100%)',
    },
    '&>div.MuiInputBase-root': {
      padding: '0!important',
      borderRadius: '4px !important',
    },
    '&>div.MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#e9ecef!important',
    },
    '&>div.MuiInputBase-root.Mui-focused': {
      boxShadow: '0 0 0 1.5px #2370ED',
    },
    '&>div>.MuiInputBase-input': {
      width: '100%!important',
      padding: '0.75rem 1.75rem 0.75rem 0.75rem!important',
      display: 'flex',
    },
    '&>div.MuiInputBase-root>svg': {
      display: 'block',
      height: '1.5em',
      width: '1.5em',
      top: 'calc(50% - 0.75em)',
    },
  }

  return (
    <NumericFormat
      label={label}
      value={value}
      inputRef={inputRef}
      id={fieldId}
      name={fieldId}
      placeholder={placeholder}
      suffix="%"
      fixedDecimalScale
      decimalScale={2}
      inputProps={{ inputMode: 'numeric' }}
      InputProps={{
        endAdornment: showAdornment && (
          <InputAdornment position="end">
            {isError ? (
              <ErrorOutline
                color="error"
                sx={{ fontSize: '24px !important' }}
              />
            ) : isSuccess ? (
              <Check color="success" sx={{ fontSize: '24px !important' }} />
            ) : null}
          </InputAdornment>
        ),
      }}
      InputLabelProps={{ shrink }}
      sx={styles}
      customInput={TextField}
      fullWidth
      onValueChange={(values) => {
        setRawValue(values.floatValue)
        onChange && onChange(values.floatValue)
      }}
      onFocus={(e) => {
        handleFocus(fieldId, e.target)
        onFocus?.(e)
      }}
      onBlur={handleOnblurWithValidation}
      onMouseEnter={(e) => handleFocus(fieldId, e.currentTarget)}
      onMouseLeave={(e) => {
        if (document.activeElement !== e.currentTarget) {
          handleBlur()
        }
      }}
      {...rest}
    />
  )
}

MuiPercentageField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  showAdornment: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  shrink: PropTypes.bool,
  id: PropTypes.string,
  name: PropTypes.string,
  enableHelpText: PropTypes.bool,
  helpText: PropTypes.string,
}

export default MuiPercentageField
